import { useState } from 'react';

import type {
  HitlActionRequest,
  HitlActionType,
  HitlItem,
} from '../services/types';

import { Button } from './Button';

export interface JsonFlattenCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

type FlattenMode = 'flatten_top' | 'flatten_all' | 'keep_string';

const MODE_COPY: Record<
  FlattenMode,
  { label: string; detail: string }
> = {
  flatten_top: {
    label: 'Flatten — top level only',
    detail:
      'Expand the top-level keys into their own columns. Nested values stay as JSON strings on their parent column. Cap-protective default — recommended when the column has > 20 distinct keys.',
  },
  flatten_all: {
    label: 'Flatten — every nested key',
    detail:
      'Recursively expand every leaf value into its own dotted-name column (e.g. address.city → ' +
      'separate column). Capped at depth 5; deeper values stay as JSON strings with an anomaly flag.',
  },
  keep_string: {
    label: "Don't flatten — keep as JSON string",
    detail:
      'Preserve the column as a single canonicalised JSON string. The cleanser still validates each row, but no expansion happens.',
  },
};

/**
 * v0.5 Stage F.2 Phase 2 — UI card for the ``json_flatten`` HITL.
 *
 * Fired by MOA's ``_flatten_json_columns`` stage when:
 *   * the column has ``inferred_type === "json"``,
 *   * the proposed ``flatten_top`` would produce > 20 distinct
 *     dotted-key paths across all rows (the auto-flatten gate),
 *   * AND the reviewer hasn't yet picked a mode for this
 *     ``(file_hash, column)`` pair.
 *
 * Reviewer picks one of three modes; backend writes a
 * ``column_policy`` audit row keyed on ``(file_hash, column)``,
 * threads the mode through CPA → MOA → flatten stage, and the
 * stage applies the chosen mode on this run.
 *
 * Lives outside HitlQueue.tsx so the existing column-datatype /
 * format-decision / table-boundary / cross-field-rule /
 * unit-conversion card paths stay untouched.
 */
export function JsonFlattenCard({
  hit,
  onAction,
  reviewerId,
}: JsonFlattenCardProps) {
  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [selected, setSelected] = useState<FlattenMode | null>(null);

  // The HITL ``candidates[0]`` carries the distinct-keys count + a
  // sample of keys for the reviewer to see what they're picking
  // from. The persisted HitlItem doesn't surface ``provenance``, so
  // the constructor stashes the structured payload here instead —
  // matching the format_decision / cross_field_rule pattern.
  const candidates = hit.candidates as
    | Array<{
        distinct_keys_count?: number;
        distinct_keys_preview?: string[];
      }>
    | null
    | undefined;
  const candidate = candidates && candidates.length > 0 ? candidates[0] : null;
  const keyCount = candidate?.distinct_keys_count ?? null;
  const keyPreview = candidate?.distinct_keys_preview ?? [];

  const onConfirm = async () => {
    if (!selected) return;
    setPending('choose_json_flatten_mode');
    setError(null);
    try {
      await onAction(hit.hit_id, {
        type: 'choose_json_flatten_mode',
        payload: { flatten_mode: selected },
        reviewer_id: reviewerId,
      });
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const radioOrder: FlattenMode[] = [
    'flatten_top',
    'flatten_all',
    'keep_string',
  ];

  return (
    <li
      data-rule-card="json-flatten"
      data-resolution-type="json_flatten"
      className={`rounded-md border bg-paper border-l-4 border-l-warn border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="m-0 text-sm font-semibold flex-1">
            <code>{hit.column}</code> · pick how to flatten
          </h3>
          <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
            json flatten
          </span>
        </div>
        {keyCount !== null && (
          <p className="text-xs text-muted m-0">
            Would expand into{' '}
            <strong className="text-ink">{keyCount}</strong>{' '}
            columns at the top level.
          </p>
        )}
        {keyPreview.length > 0 && (
          <p className="text-[11px] text-muted m-0">
            Sample keys:{' '}
            <span className="font-mono">
              {keyPreview.slice(0, 8).join(', ')}
              {keyPreview.length > 8 ? '…' : ''}
            </span>
          </p>
        )}
      </div>

      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              Flatten mode
            </legend>
            {radioOrder.map((mode) => {
              const copy = MODE_COPY[mode];
              return (
                <label
                  key={mode}
                  className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30"
                >
                  <input
                    type="radio"
                    name={`flatten-${hit.hit_id}`}
                    value={mode}
                    checked={selected === mode}
                    onChange={() => setSelected(mode)}
                    className="mt-0.5"
                    data-testid={`flatten-radio-${mode}`}
                  />
                  <span className="flex-1">
                    <span className="block font-medium text-sm">
                      {copy.label}
                    </span>
                    <span className="block text-xs text-muted mt-0.5">
                      {copy.detail}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={selected === null || pending !== null}
              data-testid="flatten-confirm"
            >
              {pending === 'choose_json_flatten_mode' ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
